/**
 * Loads DejaVu Sans font for jsPDF to support Turkish characters (ğ, ı, İ, ş, ü, ö, ç, etc.)
 * Loads both normal and bold variants so bold rows (totals) render Turkish correctly.
 * Font base64 is cached after first fetch to avoid repeated network requests.
 */

const FONT_NAME = 'DejaVuSans';
const FONT_FILE = 'DejaVuSans.ttf';
const FONT_BOLD_FILE = 'DejaVuSans-Bold.ttf';
const CACHE_KEY = '__jspdf_turkish_font_base64';
const CACHE_KEY_BOLD = '__jspdf_turkish_font_bold_base64';

/**
 * Converts ArrayBuffer to base64 string (handles large files).
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

async function fetchAndCacheFont(filename, cacheKey) {
  let base64 = typeof window !== 'undefined' ? window[cacheKey] : null;
  if (base64) return base64;

  const fontPath = `${process.env.PUBLIC_URL || ''}/fonts/${filename}`;
  const response = await fetch(fontPath);
  if (!response.ok) return null;
  const arrayBuffer = await response.arrayBuffer();
  base64 = arrayBufferToBase64(arrayBuffer);
  if (typeof window !== 'undefined') window[cacheKey] = base64;
  return base64;
}

/**
 * Loads Turkish-supporting font into jsPDF document (normal + bold).
 * Call before any text/table rendering.
 * @param {import('jspdf').jsPDF} doc - jsPDF instance
 * @returns {Promise<void>}
 */
export async function loadTurkishFont(doc) {
  const base64 = await fetchAndCacheFont(FONT_FILE, CACHE_KEY);
  if (base64) {
    doc.addFileToVFS(FONT_FILE, base64);
    doc.addFont(FONT_FILE, FONT_NAME, 'normal');
  }

  const base64Bold = await fetchAndCacheFont(FONT_BOLD_FILE, CACHE_KEY_BOLD);
  if (base64Bold) {
    doc.addFileToVFS(FONT_BOLD_FILE, base64Bold);
    doc.addFont(FONT_BOLD_FILE, FONT_NAME, 'bold');
  }
}

export const TURKISH_FONT = FONT_NAME;
